import axios from './axiosInstance';
import axiosLib from 'axios';
import { APIAsyncJob } from '../../types';

export type LogbookSubjectEntityType =
  | 'trail'
  | 'spatialFeature'
  | 'feature'
  | 'formResponse'
  | 'publicFormResponse'
  | 'log';

export type LogbookEntityType = 'trail' | 'spatialFeature' | 'feature';

export interface GlobalLogBookEntry {
  id: string;
  areaId: string;
  sports: string[];
  timestamp: number;
  recordedAt: number;
  subjectEntityType: LogbookSubjectEntityType;
  subjectEntityId: string;
  subjectEntityName: string | null;
  spatialFeatureIds: string[];
  trailIds: string[];
  featureIds: string[];
  authorId: string | null;
  authorName: string | null;
  formType: string | null;
  spatialFeatureClassSlug: string | null;
  sourceTable: string;
  sourceId: string;
  eventId: string | null;
  changedKeys: string[];
  changedValues: Record<string, unknown>;
  preview: string | null;
  createdAt: number;
}

export interface GetAreaLogbookParams {
  sport: string;
  areaId: string;
  limit?: number;
  pageCursor?: string;
  beforeTimestamp?: number;
  afterTimestamp?: number;
  entityType?: LogbookEntityType;
  entityId?: string | string[];
  includeDescendants?: boolean;
  /** Narrow the spatialFeature descendant rollup to these class slugs (a group
   *  slug covers its subtree). Requires includeDescendants. */
  descendantClassSlugs?: string[];
  authorIds?: string[];
  subjectTypes?: LogbookSubjectEntityType[];
  history?: 'none';
  formTypes?: string[] | 'none';
  logs?: 'none';
  changedKeys?: string[];
  /**
   * Sport display filter (e.g. ['BIKE']). Omit to return every sport the user
   * can see. `sport` (above) stays the path segment used for routing + the
   * per-sport permission gate; `sports` is the plural query-param narrowing.
   */
  sports?: string[];

  abortSignal?: AbortSignal;
}

export interface GetAreaLogbookResponse {
  data: GlobalLogBookEntry[];
  hasMore: boolean;
  count: number;
  pageCursor?: string;
}

/** Filter params shared by the read and export endpoints (no paging). */
const buildLogbookFilterQueryParams = ({
  beforeTimestamp,
  afterTimestamp,
  entityType,
  entityId,
  includeDescendants,
  descendantClassSlugs,
  authorIds,
  subjectTypes,
  history,
  formTypes,
  logs,
  changedKeys,
  sports,
}: Omit<GetAreaLogbookParams, 'sport' | 'areaId' | 'limit' | 'pageCursor' | 'abortSignal'>): Record<string, string | number> => {
  const queryParams: Record<string, string | number> = {};
  if (beforeTimestamp) {
    queryParams.beforeTimestamp = beforeTimestamp;
  }
  if (afterTimestamp) {
    queryParams.afterTimestamp = afterTimestamp;
  }
  if (entityType) {
    queryParams.entityType = entityType;
  }
  if (entityId) {
    queryParams.entityId = Array.isArray(entityId) ? entityId.join(',') : entityId;
  }
  if (includeDescendants) {
    queryParams.includeDescendants = 'true';
  }
  if (includeDescendants && descendantClassSlugs && descendantClassSlugs.length) {
    queryParams.descendantClassSlugs = descendantClassSlugs.join(',');
  }
  if (authorIds && authorIds.length) {
    queryParams.authorIds = authorIds.join(',');
  }
  if (subjectTypes && subjectTypes.length) {
    queryParams.subjectTypes = subjectTypes.join(',');
  }
  if (history) {
    queryParams.history = history;
  }
  if (formTypes) {
    queryParams.formTypes = Array.isArray(formTypes) ? formTypes.join(',') : formTypes;
  }
  if (logs) {
    queryParams.logs = logs;
  }
  if (changedKeys && changedKeys.length) {
    queryParams.changedKeys = changedKeys.join(',');
  }
  if (sports && sports.length) {
    queryParams.sports = sports.join(',');
  }
  return queryParams;
};

const getAreaLogbook = ({
  sport,
  areaId,
  limit,
  pageCursor,
  abortSignal,
  ...filters
}: GetAreaLogbookParams): Promise<GetAreaLogbookResponse> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  const url = `/${sport}/areas/${areaId}/logbook`;
  const queryParams = buildLogbookFilterQueryParams(filters);
  if (limit) {
    queryParams.limit = limit;
  }
  if (pageCursor) {
    queryParams.pageCursor = pageCursor;
  }

  return axios.get(url, {
    cancelToken: source.token,
    params: queryParams,
  })
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

export type GetAreaLogbookExportParams = Omit<GetAreaLogbookParams, 'limit' | 'pageCursor'>;

/**
 * Kick off an async export of the logbook (zip of per-entry PDFs/CSVs plus a
 * manifest.csv). Accepts the same filters as getAreaLogbook so the export
 * matches the current table view; returns the AsyncJob to poll
 * (AsyncJobPollingModal handles polling + download).
 */
const getAreaLogbookExport = ({
  sport,
  areaId,
  abortSignal,
  ...filters
}: GetAreaLogbookExportParams): Promise<APIAsyncJob> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  const url = `/${sport}/areas/${areaId}/logbook/export`;
  return axios.get(url, {
    cancelToken: source.token,
    params: buildLogbookFilterQueryParams(filters),
  })
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

/**
 * Kick off an async CSV export of ONE form type's responses (ULLR-67): one
 * row per response, one column per question. Same filters as
 * getAreaLogbookExport; `formTypes` must hold exactly one non-park-log type
 * (the API answers 400 otherwise). The job's artifact is a bare .csv, which
 * AsyncJobPollingModal downloads like the zip.
 */
const getAreaLogbookCsvExport = ({
  sport,
  areaId,
  abortSignal,
  ...filters
}: GetAreaLogbookExportParams): Promise<APIAsyncJob> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  const url = `/${sport}/areas/${areaId}/logbook/export/csv`;
  return axios.get(url, {
    cancelToken: source.token,
    params: buildLogbookFilterQueryParams(filters),
  })
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

export default {
  getAreaLogbook,
  getAreaLogbookExport,
  getAreaLogbookCsvExport,
};
